import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { MessageService } from "@openng/optimus-ui/api";
import { ComponentView } from "src/modules/app-template/models/component-view.model";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { Objects } from "src/modules/utils/shared/objects";
import { RestEndpoint } from "../../models/rest-endpoint.model";
import { FrontendField } from "../../netgen/FrontendField";
import { FrontendFieldType } from "../../netgen/FrontendFieldType";
import { FrontendForm } from "../../netgen/FrontendForm";
import { UserLoginDto } from "../../netgen/UserLoginDto";
import { UserSetInfoPriorToLoginDto } from "../../netgen/UserSetInfoPriorToLoginDto";
import { UserSetInfoPriorToLoginResultDto } from "../../netgen/UserSetInfoPriorToLoginResultDto";
import { AuthLoginResult } from "../../shared/AuthLoginResult";
import { FirstLoginComponent } from "../first-login/first-login.component";

@Component({
    selector: "sm-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent extends ComponentView {

    app = inject(MainAppService);
    messageService = inject(MessageService);

    form: FrontendForm;
    inputInformationForm: FrontendForm;

    waiting = false;
    loginChallengeId: string;

    status: "login" | "inputInformation" = "login";

    async initParams(): Promise<boolean> {
        this.form = new FrontendForm([
            new FrontendField("userName", "Benutzername", FrontendFieldType.string, {
                mandatory: true,
                titleAsPlaceholder: true,
                hasLabel: false
            }),
            new FrontendField("password", "Passwort", FrontendFieldType.password, {
                mandatory: true,
                        titleAsPlaceholder: true,
                hasLabel: false
            }),
            new FrontendField("secondFactor", "Bestätigungs-Code", FrontendFieldType.string, {
                visible: false,
                mandatory: true
            }),
            new FrontendField("send", "Login", FrontendFieldType.button, {
                submitButton: true,
                icon: "fas fa-sign-in",
                onClick: this.send.bind(this)
            }),
            new FrontendField("resendSecondFactor", "Code erneut senden", FrontendFieldType.button, {
                visible: false,
                icon: "fas fa-envelope",
                onClick: this.resendSecondFactor.bind(this)
            }),
            this.app.mainConfig.authRules.canRevokePassword ? new FrontendField("resetPassword", "Passwort vergessen", FrontendFieldType.button, {
                icon: "fas fa-lock",
                onClick: this.resetPassword.bind(this)
            }) : null,
            new FrontendField("waiting", "", FrontendFieldType.spinner, {
                visible: false,
                fixedHeight: "30px"
            })
        ], { submitOnEnter: true });

        return Promise.resolve(true);
    }

    async send(): Promise<void> {
        if (!this.app.validateForm(this.form, null, this.messageService)) {
            return;
        }

        const userName = this.form.getValueString("userName").trim();
        const password = this.loginChallengeId == null ? this.form.getValueString("password").trim() : "";
        this.setWaiting(true);
        try {
            const loginUser = await this.app.auth.login(Objects.fromPlain(UserLoginDto, {
                userName,
                password,
                secondFactor: this.form.getValueString("secondFactor")?.trim(),
                loginChallengeId: this.loginChallengeId,
                resendSecondFactor: false
            }));
            switch (loginUser.loginResult) {
                case AuthLoginResult.ok:
                    try {
                        await this.app.navigateToUrl((this.route.snapshot.queryParams.returnUrl as string) || "/", true, { [MainAppService.navigationStateFromLogin]: true });
                    }
                    catch {
                        await this.app.navigateTo(["/"], true);
                    }
                    if (loginUser.loginResultData?.passwordWasCreated) {
                        this.app.showToast("success", "Passwort vergeben", "Ihr Passwort wurde erfolgreich vergeben.");
                    }
                    break;
                case AuthLoginResult.unspecifiedError:
                    this.app.showErrorToast("Beim Login kam es zu einem unbekannten Fehler", "Anmeldung fehlgeschlagen", null, false, this.messageService);
                    break;
                case AuthLoginResult.invalidUserName:
                case AuthLoginResult.invalidPassword:
                    this.app.showErrorToast("Die Kombination aus Benutzername und Passwort ist unbekannt", "Anmeldung fehlgeschlagen", null, false, this.messageService);
                    break;
                case AuthLoginResult.accountLocked:
                case AuthLoginResult.accountJustLocked:
                    this.app.showErrorToast("Ihr Benutzerkonto ist gesperrt. Bitte wenden Sie sich an einen Administrator", "Anmeldung fehlgeschlagen", null, false, this.messageService);
                    break;
                case AuthLoginResult.userInactive:
                    this.app.showErrorToast("Das Benutzerkonto wurde deaktiviert", "Anmeldung fehlgeschlagen", null, false, this.messageService);
                    break;
                case AuthLoginResult.mustChangePassword:
                case AuthLoginResult.answerQuestions:
                    FirstLoginComponent.currentPassword = password;
                    await this.app.navigateTo(["base", "firstlogin"], null, { userName });
                    break;
                case AuthLoginResult.secondFactorNeeded:
                    this.handleSecondFactorNeeded(loginUser.loginResultData);
                    break;
                case AuthLoginResult.secondFactorInvalid: {
                    this.form.setValue("secondFactor", "");
                    const remainingAttempts = loginUser.loginResultData?.remainingAttempts;
                    this.app.showErrorToast("Der Bestätigungs-Code ist ungültig" +
                        (remainingAttempts == null ? "" : ". Verbleibende Versuche: " + remainingAttempts), null, null, false, this.messageService);
                    break;
                }
                case AuthLoginResult.secondFactorExpired:
                    this.resetSecondFactor();
                    this.app.showErrorToast(loginUser.loginResultData?.attemptLimitReached ?
                        "Der Bestätigungs-Code wurde zu oft falsch eingegeben. Bitte melden Sie sich erneut an" :
                        "Der Bestätigungs-Code ist abgelaufen. Bitte melden Sie sich erneut an", null, null, false, this.messageService);
                    break;
                case AuthLoginResult.needsInformation:
                    this.inputInformationForm = Objects.fromPlainUnsafe(FrontendForm, loginUser.loginResultData);
                    this.inputInformationForm.fields.push(new FrontendField("send", "Abschicken", FrontendFieldType.button, {
                        submitButton: true,
                        icon: "fas fa-send",
                        onClick: this.sendInformation.bind(this)
                    }));
                    this.status = "inputInformation";
                    break;
            }
        }
        finally {
            this.setWaiting(false);
        }
    }

    async resendSecondFactor(): Promise<void> {
        if (this.loginChallengeId == null) {
            return;
        }

        this.setWaiting(true);
        try {
            const loginUser = await this.app.auth.login(Objects.fromPlain(UserLoginDto, {
                userName: this.form.getValueString("userName").trim(),
                password: "",
                secondFactor: "",
                loginChallengeId: this.loginChallengeId,
                resendSecondFactor: true
            }));
            if (loginUser.loginResult == AuthLoginResult.secondFactorNeeded) {
                this.handleSecondFactorNeeded(loginUser.loginResultData);
            }
            else if (loginUser.loginResult == AuthLoginResult.secondFactorExpired) {
                this.resetSecondFactor();
                this.app.showErrorToast("Die Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an", null, null, false, this.messageService);
            }
        }
        finally {
            this.setWaiting(false);
        }
    }

    async sendInformation(): Promise<void> {
        if (!this.app.validateForm(this.inputInformationForm, null, this.messageService)) {
            return;
        }

        const data = Objects.fromPlain(UserSetInfoPriorToLoginDto, {
            userName: this.form.getValueString("userName").trim(),
            password: this.form.getValueString("password").trim(),
            data: this.inputInformationForm.get({})
        });

        const res = await RestEndpoint.main().body(data).post().run("api/app/auth/setInfoPriorToLogin").get(UserSetInfoPriorToLoginResultDto);
        if (res.ok) {
            this.status = "login";
            this.app.showInfoToast(res.message, null, null, false, this.messageService);
            await this.send();
        }
        else {
            this.app.showErrorToast(res.message, null, null, false, this.messageService);
        }
    }

    async resetPassword(): Promise<void> {
        await this.app.navigate(["/base", "resetpassword"]);
    }

    private handleSecondFactorNeeded(data: any): void {
        this.form.getField("secondFactor").visible = true;
        if (data?.loginChallengeId == null) {
            this.app.showInfoToast("Wir haben Ihnen einen Bestätigungscode per E-Mail gesendet. Bitte geben Sie den Code ein, um den Login abzuschließen.", "Bestätigung notwendig", null, false, this.messageService);
            return;
        }

        this.loginChallengeId = data.loginChallengeId;
        this.form.setValue("password", "");
        this.form.getField("password").visible = false;
        this.form.getField("userName").enabled = false;
        this.form.getField("resendSecondFactor").visible = true;
        const resetPassword = this.form.getField("resetPassword", false);
        if (resetPassword != null) {
            resetPassword.visible = false;
        }

        if (data?.emailSent) {
            this.app.showInfoToast("Wir haben Ihnen einen Bestätigungscode per E-Mail gesendet. Bitte geben Sie den Code ein, um den Login abzuschließen.", "Bestätigung notwendig", null, false, this.messageService);
        }
        else if (data?.challengeReused) {
            this.app.showInfoToast("Für diese Anmeldung wurde bereits ein Bestätigungscode versendet. Bitte verwenden Sie diesen Code oder fordern Sie nach Ablauf der Wartezeit einen neuen an.", "Bestätigung notwendig", null, false, this.messageService);
        }
        else if (data?.deliveryLimited) {
            this.app.showInfoToast("Ein neuer Bestätigungscode kann in " + data.retryAfterSeconds + " Sekunden angefordert werden.", "Versand begrenzt", null, false, this.messageService);
        }
    }

    private resetSecondFactor(): void {
        this.loginChallengeId = null;
        this.form.setValue("secondFactor", "");
        this.form.getField("secondFactor").visible = false;
        this.form.getField("resendSecondFactor").visible = false;
        this.form.getField("password").visible = true;
        this.form.getField("userName").enabled = true;
        const resetPassword = this.form.getField("resetPassword", false);
        if (resetPassword != null) {
            resetPassword.visible = true;
        }
    }

    private setWaiting(value: boolean): void {
        this.form.getField("waiting").visible = value;
        this.form.getField("send").enabled = !value;
        this.form.getField("resendSecondFactor").enabled = !value;
    }
}
